// Live implementation of LeaveDataService against SharePoint lists via Microsoft
// Graph (Phase 4.5 of the build guide, extended with decide/cancel).
//
// IMPORTANT: SharePoint field *internal names* can differ from display names
// (spaces become _x0020_, renamed columns keep their original internal name,
// etc.). Verify each field name below with:
//   GET /sites/{siteId}/lists/{listId}/columns
// once your real lists exist, and adjust the mappings if needed.
//
// Side-effect policy: approval side effects (balance deduction, calendar events,
// employee notifications) are owned by Power Automate's "Decision Processing"
// flow, which fires whenever a request's Status changes. So decideLeaveRequest
// here only patches the record — deciding in the portal and on the Teams card
// end up on the same path. Cancellation IS owned by the app (Phase 8.3), so it
// refunds + deletes.

import type { Client } from "@microsoft/microsoft-graph-client";
import { LeaveSystemConfig } from "../config/leaveSystemConfig";
import {
  CalendarEvent,
  CurrentUser,
  Employee,
  Holiday,
  LeaveBalance,
  LeaveRequest,
  LeaveStatus,
  LeaveType,
  LeaveTypeCode,
} from "../models/leaveModels";
import { Decision, LeaveDataService } from "./leaveService";
import { isoToLocalDate, parseDateList } from "../utils/dateUtils";
import { eventInWindow, requestToEvents } from "../utils/calendarEvents";
import { workingDaysInRange } from "../utils/workingDays";
import { isApproverDepartment } from "../config/leavePolicy";

const { siteId, groupId, lists } = LeaveSystemConfig;

// Escape single quotes for OData string literals.
const odata = (s: string) => s.replace(/'/g, "''");

const PREFER_NONINDEXED = "HonorNonIndexedQueriesWarningMayFailRandomly";

export class GraphLeaveService implements LeaveDataService {
  private codeByLookupId = new Map<number, LeaveTypeCode>();

  constructor(private graph: Client) {}

  private listUrl(listId: string): string {
    return `/sites/${siteId}/lists/${listId}/items`;
  }

  async getCurrentUser(): Promise<CurrentUser> {
    const me = await this.graph.api("/me").select("displayName,mail,userPrincipalName").get();
    return {
      name: me.displayName,
      email: me.mail ?? me.userPrincipalName,
    };
  }

  /** The signed-in user's Entra profile photo as an object URL (null if none). */
  async getMyPhotoUrl(): Promise<string | null> {
    try {
      const res = await this.graph.api("/me/photo/$value").get();
      return res instanceof Blob ? URL.createObjectURL(res) : null;
    } catch {
      return null; // no photo set, or not permitted — caller falls back to initials
    }
  }

  /** Fetch an attachment by its SharePoint webUrl and return an object URL for
   * in-app preview. Uses the Graph "shares" API to resolve the URL to a drive
   * item and stream its content with the user's delegated token. */
  async getAttachmentBlobUrl(webUrl: string): Promise<string | null> {
    if (!webUrl) return null;
    try {
      const b64 = btoa(webUrl).replace(/=+$/, "").replace(/\//g, "_").replace(/\+/g, "-");
      const res = await this.graph.api(`/shares/u!${b64}/driveItem/content`).get();
      return res instanceof Blob ? URL.createObjectURL(res) : null;
    } catch {
      return null;
    }
  }

  /** Another user's Entra profile photo by email (null if none / not permitted). */
  async getPhotoUrlForEmail(email: string): Promise<string | null> {
    if (!email || !email.includes("@")) return null;
    try {
      const res = await this.graph.api(`/users/${encodeURIComponent(email)}/photo/$value`).get();
      return res instanceof Blob ? URL.createObjectURL(res) : null;
    } catch {
      return null;
    }
  }

  async getEmployeeByEmail(email: string): Promise<Employee | null> {
    const res = await this.graph
      .api(this.listUrl(lists.employees))
      .header("Prefer", PREFER_NONINDEXED)
      .expand("fields")
      .filter(`fields/Email eq '${odata(email)}'`)
      .top(1)
      .get();
    const item = res.value?.[0];
    return item ? this.toEmployee(item) : null;
  }

  async getLeaveTypes(): Promise<LeaveType[]> {
    const res = await this.graph
      .api(this.listUrl(lists.leaveTypes))
      .expand("fields")
      .top(50)
      .get();
    const types = res.value.map((i: any) => this.toLeaveType(i));
    this.codeByLookupId.clear();
    for (const t of types) this.codeByLookupId.set(t.lookupId, t.code);
    return types;
  }

  private async ensureTypeMap(): Promise<void> {
    if (this.codeByLookupId.size === 0) await this.getLeaveTypes();
  }

  async getHolidays(year: number): Promise<Holiday[]> {
    const res = await this.graph
      .api(this.listUrl(lists.holidays))
      .header("Prefer", PREFER_NONINDEXED)
      .expand("fields")
      .filter(`fields/Year eq ${year}`)
      .top(100)
      .get();
    return res.value.map((i: any) => this.toHoliday(i));
  }

  async getMyLeaveRequests(userEmail: string): Promise<LeaveRequest[]> {
    await this.ensureTypeMap();
    const res = await this.graph
      .api(this.listUrl(lists.leaveRequests))
      .header("Prefer", PREFER_NONINDEXED)
      .expand("fields")
      .filter(`fields/EmployeeEmail eq '${odata(userEmail)}'`)
      .top(50)
      .get();
    return this.withEmployeeNames(res.value.map((i: any) => this.toRequest(i)));
  }

  async getPendingForManager(managerEmail: string): Promise<LeaveRequest[]> {
    await this.ensureTypeMap();
    const res = await this.graph
      .api(this.listUrl(lists.leaveRequests))
      .header("Prefer", PREFER_NONINDEXED)
      .expand("fields")
      .filter(
        `fields/ManagerEmail eq '${odata(managerEmail)}' and fields/Status eq 'Pending'`,
      )
      .top(100)
      .get();
    return this.withEmployeeNames(res.value.map((i: any) => this.toRequest(i)));
  }

  async getMyBalances(userEmail: string, year: number): Promise<LeaveBalance[]> {
    // Assumes the LeaveBalances list carries denormalized EmployeeEmail + Code
    // text columns (recommended in docs/config-ids.template.md) for filtering.
    const res = await this.graph
      .api(this.listUrl(lists.leaveBalances))
      .header("Prefer", PREFER_NONINDEXED)
      .expand("fields")
      .filter(`fields/EmployeeEmail eq '${odata(userEmail)}' and fields/Year eq ${year}`)
      .top(50)
      .get();
    return res.value.map((i: any) => this.toBalance(i));
  }

  async createLeaveRequest(request: LeaveRequest, attachment?: File): Promise<LeaveRequest> {
    await this.ensureTypeMap();
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const now = new Date().toISOString();
    const title = `LR-${stamp}-${Date.now() % 100000}`;
    // Upload BEFORE creating the item: the approval flow fires on "item
    // created", so the link has to be on the item from the start.
    const attachmentLink = attachment
      ? await this.uploadAttachment(title, request.startDate.slice(0, 4), attachment)
      : undefined;
    const payload = {
      fields: {
        Title: title,
        EmployeeEmail: request.employeeEmail,
        LeaveTypeLookupId: request.leaveTypeId,
        StartDate: request.startDate,
        EndDate: request.endDate,
        // Multiple lines of text column: the exact (possibly non-consecutive) days.
        LeaveDates: request.leaveDates.join(","),
        WorkingDays: request.workingDays,
        LWOPDays: request.lwopDays,
        Reason: request.reason,
        Status: "Pending",
        FiledDate: now,
        PendingSince: now,
        ManagerEmail: request.managerEmail,
        // Plain-text column: Graph can't write SharePoint Hyperlink columns.
        ...(attachmentLink ? { AttachmentLink: attachmentLink } : {}),
      },
    };
    const created = await this.graph.api(this.listUrl(lists.leaveRequests)).post(payload);
    return this.toRequest(created);
  }

  async decideLeaveRequest(
    requestId: string,
    decision: Decision,
    approverEmail: string,
    notes: string,
  ): Promise<void> {
    // Fallback/audit path only — balance + calendar side effects run in the flow.
    await this.graph
      .api(`${this.listUrl(lists.leaveRequests)}/${requestId}/fields`)
      .patch({
        Status: decision,
        DecisionDate: new Date().toISOString(),
        DecisionNotes: notes,
        // Approver is a person field; set it via LookupId if you resolve the
        // approver's SharePoint user id. Text mirror kept simple here.
        ApproverEmail: approverEmail,
      });
  }

  async cancelLeaveRequest(requestId: string): Promise<void> {
    await this.ensureTypeMap();
    // Read current state so we can refund + delete the calendar event.
    const item = await this.graph
      .api(`${this.listUrl(lists.leaveRequests)}/${requestId}`)
      .expand("fields")
      .get();
    const req = this.toRequest(item);

    await this.graph
      .api(`${this.listUrl(lists.leaveRequests)}/${requestId}/fields`)
      .patch({ Status: "Cancelled", DecisionDate: new Date().toISOString() });

    const wasApproved = req.status === "Approved" || req.status === "Auto-Approved";

    // Only touch the group calendar when we hold the group-calendar scopes.
    // Otherwise a Power Automate "on cancel" flow should delete the event.
    // The flow stores one event id per run of consecutive days, comma-separated.
    if (
      LeaveSystemConfig.useGroupCalendar &&
      wasApproved &&
      req.calendarEventId &&
      groupId
    ) {
      for (const eventId of req.calendarEventId.split(",").map((s) => s.trim()).filter(Boolean)) {
        try {
          await this.graph.api(`/groups/${groupId}/calendar/events/${eventId}`).delete();
        } catch {
          // Event may already be gone; ignore.
        }
      }
    }

    if (wasApproved) {
      await this.refundBalance(req);
    }
  }

  async getTeamCalendarEvents(
    startISO: string,
    endISO: string,
  ): Promise<CalendarEvent[]> {
    // Default: derive the team calendar from approved rows in LeaveRequests
    // (Sites scope only). Set REACT_APP_USE_GROUP_CALENDAR=true to read the
    // M365 group calendar directly (needs Group.Read.All admin consent).
    if (LeaveSystemConfig.useGroupCalendar) {
      const res = await this.graph
        .api(`/groups/${groupId}/calendarView`)
        .query({ startDateTime: startISO, endDateTime: endISO })
        .top(200)
        .get();
      return (res.value as any[]).map((e) => this.toCalendarEvent(e));
    }

    await this.ensureTypeMap();
    const res = await this.graph
      .api(this.listUrl(lists.leaveRequests))
      .header("Prefer", PREFER_NONINDEXED)
      .expand("fields")
      .filter("fields/Status eq 'Approved' or fields/Status eq 'Auto-Approved'")
      .top(500)
      .get();

    const requests = await this.withEmployeeNames(
      (res.value as any[]).map((i) => this.toRequest(i)),
    );
    return requests.flatMap(requestToEvents).filter((e) => eventInWindow(e, startISO, endISO));
  }

  // --- employee names ------------------------------------------------------

  private employeeNames: Promise<Map<string, string>> | null = null;

  /**
   * Email -> display name from the Employees list (its Title column), loaded
   * once per session. Requests only store EmployeeEmail, so without this the
   * Manager Queue and Team Calendar would show email addresses.
   */
  private loadEmployeeNames(): Promise<Map<string, string>> {
    if (!this.employeeNames) {
      this.employeeNames = (async () => {
        const names = new Map<string, string>();
        let res = await this.graph
          .api(this.listUrl(lists.employees))
          .expand("fields($select=Title,Email)")
          .top(500)
          .get();
        for (;;) {
          for (const item of res.value as any[]) {
            const email = String(item.fields?.Email ?? "").toLowerCase();
            if (email && item.fields?.Title) names.set(email, String(item.fields.Title));
          }
          const next = res["@odata.nextLink"];
          if (!next) break;
          res = await this.graph.api(next).get();
        }
        return names;
      })().catch((err) => {
        this.employeeNames = null; // retry next time instead of caching the failure
        throw err;
      });
    }
    return this.employeeNames;
  }

  /** Replace email-only employee labels with names from the Employees list. */
  private async withEmployeeNames(requests: LeaveRequest[]): Promise<LeaveRequest[]> {
    if (requests.length === 0) return requests;
    let names: Map<string, string>;
    try {
      names = await this.loadEmployeeNames();
    } catch {
      return requests; // names are cosmetic — fall back to what the request has
    }
    return requests.map((r) => ({
      ...r,
      employee: names.get(r.employeeEmail.toLowerCase()) ?? r.employee,
    }));
  }

  // --- attachments ---------------------------------------------------------

  private attachmentDriveId: string | null = null;

  /** The "Medical Certificates" document library's drive id (looked up by name once). */
  private async getAttachmentDriveId(): Promise<string> {
    if (this.attachmentDriveId) return this.attachmentDriveId;
    const name = LeaveSystemConfig.attachmentLibrary;
    const res = await this.graph.api(`/sites/${siteId}/drives`).select("id,name").get();
    const drive = (res.value as any[]).find(
      (d) => String(d.name).toLowerCase() === name.toLowerCase(),
    );
    if (!drive) {
      throw new Error(
        `The "${name}" document library doesn't exist on the KTI Leave System site yet, so the attachment can't be saved. Ask the site owner to create it.`,
      );
    }
    this.attachmentDriveId = String(drive.id);
    return this.attachmentDriveId;
  }

  /**
   * Upload a file into the attachments library under a year folder, named after
   * the request (e.g. "2026/LR-20260915-12345 - medcert.pdf"). Returns its link.
   */
  private async uploadAttachment(title: string, year: string, file: File): Promise<string> {
    const driveId = await this.getAttachmentDriveId();
    // SharePoint rejects " * : < > ? / \ | in names; brackets/# % would break links.
    const fileName = `${title} - ${file.name}`
      .replace(/["*:<>?/\\|#%()[\]{}]/g, "_")
      .replace(/^[\s.]+|[\s.]+$/g, "");
    const path = [year, fileName].map(encodeURIComponent).join("/");
    const item = await this.graph
      .api(`/drives/${driveId}/root:/${path}:/content`)
      .query({ "@microsoft.graph.conflictBehavior": "rename" })
      .header("Content-Type", file.type || "application/octet-stream")
      .put(file);
    return String(item.webUrl);
  }

  // --- refund helper -------------------------------------------------------

  private async refundBalance(req: LeaveRequest): Promise<void> {
    // LWOP days were never deducted, so only the paid days come back.
    const paidDays = Math.max(0, req.workingDays - req.lwopDays);
    if (paidDays === 0) return;
    const year = Number(req.startDate.slice(0, 4));
    const res = await this.graph
      .api(this.listUrl(lists.leaveBalances))
      .header("Prefer", PREFER_NONINDEXED)
      .expand("fields")
      .filter(
        `fields/EmployeeEmail eq '${odata(req.employeeEmail)}' and fields/Year eq ${year} and fields/Code eq '${odata(
          req.leaveTypeCode,
        )}'`,
      )
      .top(1)
      .get();
    const item = res.value?.[0];
    if (!item) return;
    const used = Math.max(0, (item.fields.Used ?? 0) - paidDays);
    const entitlement = item.fields.Entitlement ?? 0;
    await this.graph
      .api(`${this.listUrl(lists.leaveBalances)}/${item.id}/fields`)
      .patch({ Used: used, Remaining: entitlement - used });
  }

  // --- mappers -------------------------------------------------------------

  private toEmployee(item: any): Employee {
    const f = item.fields;
    // Department is a choice column; if it allows multiple selections Graph
    // returns an array — prefer an approver department when one is present.
    const departments: string[] = [].concat(f.Department ?? []);
    return {
      id: item.id,
      name: f.Title,
      email: f.Email,
      department: (departments.find(isApproverDepartment) ?? departments[0]) as Employee["department"],
      managerName: f.Manager?.LookupValue,
      managerEmail: f.ManagerEmail ?? "",
      hireDate: f.HireDate ? isoToLocalDate(String(f.HireDate)) : undefined,
      probationStatus: Boolean(f.ProbationStatus),
      vlEntitlement: f.VLEntitlement ?? 0,
      slEntitlement: f.SLEntitlement ?? 0,
    };
  }

  private toLeaveType(item: any): LeaveType {
    const f = item.fields;
    return {
      id: item.id,
      lookupId: Number(item.id),
      title: f.Title,
      code: f.Code as LeaveTypeCode,
      requiresMedCert: Boolean(f.RequiresMedCert),
      medCertThresholdDays: f.MedCertThresholdDays ?? 0,
      requiresDocuments: Boolean(f.RequiresDocuments),
      deductsBalance: Boolean(f.DeductsBalance),
      availableDuringProbation: Boolean(f.AvailableDuringProbation),
      leadTimeDays: f.LeadTimeDays ?? 0,
    };
  }

  private toBalance(item: any): LeaveBalance {
    const f = item.fields;
    return {
      id: item.id,
      employeeEmail: f.EmployeeEmail ?? "",
      year: f.Year ?? 0,
      leaveTypeCode: (f.Code ?? f.LeaveTypeCode) as LeaveTypeCode,
      entitlement: f.Entitlement ?? 0,
      used: f.Used ?? 0,
      remaining: f.Remaining ?? (f.Entitlement ?? 0) - (f.Used ?? 0),
    };
  }

  private toHoliday(item: any): Holiday {
    const f = item.fields;
    return {
      id: item.id,
      name: f.Title,
      date: isoToLocalDate(f.HolidayDate),
      type: f.HolidayType,
      year: f.Year,
    };
  }

  private toRequest(item: any): LeaveRequest {
    const f = item.fields;
    const lookupId = f.LeaveTypeLookupId ?? f.LeaveType_x003a__x0020_LookupId;
    const startDate = isoToLocalDate(f.StartDate);
    const endDate = isoToLocalDate(f.EndDate);
    // Rows filed before the LeaveDates column existed: every weekday in range.
    const stored = parseDateList(f.LeaveDates);
    const leaveDates = stored.length ? stored : workingDaysInRange(startDate, endDate, []);
    return {
      id: item.id,
      title: f.Title,
      employee: f.Employee?.LookupValue ?? f.EmployeeEmail ?? "",
      employeeEmail: f.EmployeeEmail ?? "",
      leaveTypeId: Number(lookupId ?? 0),
      leaveTypeCode:
        this.codeByLookupId.get(Number(lookupId)) ??
        (f.LeaveTypeCode as LeaveTypeCode),
      leaveDates,
      startDate,
      endDate,
      workingDays: f.WorkingDays ?? leaveDates.length,
      lwopDays: Number(f.LWOPDays ?? 0),
      reason: f.Reason ?? "",
      status: (f.Status ?? "Pending") as LeaveStatus,
      filedDate: f.FiledDate,
      pendingSince: f.PendingSince,
      managerEmail: f.ManagerEmail ?? "",
      approver: f.Approver?.LookupValue ?? f.ApproverEmail,
      decisionDate: f.DecisionDate,
      decisionNotes: f.DecisionNotes,
      attachmentUrl: f.AttachmentLink ?? f.AttachmentUrl?.Url ?? f.AttachmentUrl,
      calendarEventId: f.CalendarEventId,
    };
  }

  private toCalendarEvent(e: any): CalendarEvent {
    // Subject convention from the flow: "{Employee Name} — {CODE}"
    const subject: string = e.subject ?? "";
    const [name, code] = subject.split("—").map((s) => s.trim());
    return {
      id: e.id,
      subject,
      start: e.start?.dateTime ?? e.start,
      end: e.end?.dateTime ?? e.end,
      employeeName: name || undefined,
      leaveTypeCode: (code as LeaveTypeCode) || undefined,
    };
  }
}
